import React, { useState, useEffect } from 'react';
import { getPageContent, subscribeToPage } from '../firebase/firebase-config';
import '../styles/rich-text.css';

function Sports() {
  const [content, setContent] = useState({ 
    sportsOverview: '', 
    sports: [], 
    sportsAchievements: [], 
    sportsVideos: [], 
    sliderImages: [] 
  });
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    primaryColor: '#1a3a6b',
    secondaryColor: '#f59e0b'
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [sportsData, settingsData] = await Promise.all([
          getPageContent('sports'),
          import('../firebase/firebase-config').then(module => module.getSettings())
        ]);
        setContent(sportsData || {});
        if (settingsData) setSettings(settingsData);
      } catch (error) { 
        console.error(error); 
      } finally { 
        setLoading(false); 
      }
    };
    loadData();
    const unsubscribe = subscribeToPage('sports', (data) => { 
      if (data) setContent(data); 
    });
    return () => unsubscribe();
  }, []);

  const getYouTubeId = (url) => { 
    const match = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?#]+)/); 
    return match ? match[1] : null; 
  };

  if (loading) return (
    <div className="loading">
      <div className="spinner"></div>
    </div>
  );

  const primaryColor = settings.primaryColor || '#1a3a6b';
  const secondaryColor = settings.secondaryColor || '#f59e0b';

  // Get slider images
  const sliderImages = content.sliderImages || [];
  const sports = content.sports || [];
  const sportsAchievements = content.sportsAchievements || [];
  const sportsVideos = content.sportsVideos || [];

  return (
    <div className="sports-page">
      {/* Banner with slider images */}
      {sliderImages.length > 0 && (
        <div className="page-banner">
          <div className="banner-slider">
            {sliderImages.map((img, idx) => (
              <div key={idx} className="banner-slide">
                <img src={img.url} alt={`Sports ${idx + 1}`} />
              </div>
            ))}
          </div>
          <div className="banner-overlay">
            <h1>Sports at MIS</h1>
          </div>
        </div>
      )}

      <div className="container">
        {/* Sports Overview with Rich Text */}
        {content.sportsOverview && (
          <div className="overview">
            <h2 style={{ color: primaryColor }}>Sports Program</h2>
            <div className="divider" style={{ background: secondaryColor }}></div>
            <div 
              className="rich-text-content"
              dangerouslySetInnerHTML={{ __html: content.sportsOverview }}
            />
          </div>
        )}

        {/* Sports Offered Section */}
        {sports.length > 0 && (
          <>
            <h2 style={{ color: primaryColor }}>Sports Offered</h2>
            <div className="divider" style={{ background: secondaryColor }}></div>
            <div className="sports-grid">
              {sports.map((sport) => (
                <div key={sport.id} className="sport-card">
                  <div className="sport-icon">{sport.icon || '⚽'}</div>
                  <h3 style={{ color: primaryColor }}>{sport.name}</h3>
                  {sport.description && (
                    <div 
                      className="rich-text-content"
                      dangerouslySetInnerHTML={{ __html: sport.description }}
                    />
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Sports Achievements Section */}
        {sportsAchievements.length > 0 && (
          <>
            <h2 style={{ color: primaryColor }}>Our Achievements</h2>
            <div className="divider" style={{ background: secondaryColor }}></div>
            <div className="achievements-grid">
              {sportsAchievements.map((ach, idx) => (
                <div key={idx} className="achievement-card">
                  {ach.image && (
                    <div className="achievement-image">
                      <img src={ach.image} alt={ach.title} />
                    </div>
                  )}
                  <span className="year" style={{ background: secondaryColor }}>{ach.year}</span>
                  <h4>{ach.title}</h4>
                  {ach.description && (
                    <div 
                      className="rich-text-content"
                      dangerouslySetInnerHTML={{ __html: ach.description }}
                    />
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Sports Videos Section */}
        {sportsVideos.length > 0 && (
          <>
            <h2 style={{ color: primaryColor }}>Sports Videos</h2>
            <div className="divider" style={{ background: secondaryColor }}></div>
            <div className="videos-grid">
              {sportsVideos.map((video, idx) => {
                const videoId = getYouTubeId(video.url);
                return videoId ? (
                  <div key={idx} className="video-card">
                    <iframe 
                      src={`https://www.youtube.com/embed/${videoId}`} 
                      title={video.title} 
                      frameBorder="0" 
                      allowFullScreen
                    ></iframe>
                    <h4>{video.title}</h4>
                  </div>
                ) : null;
              })}
            </div>
          </>
        )}
      </div>

      <style>{`
        .sports-page {
          background: #f8fafc;
          min-height: 100vh;
        }

        .loading {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 60vh;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #e2e8f0;
          border-top-color: ${primaryColor};
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Page Banner */
        .page-banner {
          position: relative;
          height: 400px;
          overflow: hidden;
        }

        .banner-slider {
          width: 100%;
          height: 100%;
        }

        .banner-slide {
          width: 100%;
          height: 100%;
        }

        .banner-slide img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          background: #000;
        }

        .banner-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
        }

        .banner-overlay h1 {
          color: white;
          font-size: 48px;
          margin: 0;
          animation: fadeInUp 0.8s ease;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 60px 24px;
        }

        h2 {
          font-size: 32px;
          text-align: center;
          margin: 40px 0 15px;
        }

        .divider {
          width: 80px;
          height: 4px;
          margin: 0 auto 30px;
          border-radius: 2px;
        }

        /* Overview Section */
        .overview {
          background: white;
          padding: 40px;
          border-radius: 20px;
          margin-bottom: 50px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }

        .overview h2 {
          margin-top: 0;
        }

        /* Sports Grid */
        .sports-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 30px;
          margin-bottom: 50px;
        }

        .sport-card {
          background: white;
          padding: 30px 25px;
          border-radius: 20px;
          text-align: center;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
          transition: transform 0.3s, box-shadow 0.3s;
        }

        .sport-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
        }

        .sport-icon {
          font-size: 52px;
          margin-bottom: 15px;
        }

        .sport-card h3 {
          font-size: 22px;
          margin-bottom: 12px;
        }

        /* Achievements Grid */
        .achievements-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 30px;
          margin-bottom: 50px;
        }

        .achievement-card {
          background: white;
          padding: 25px;
          border-radius: 16px;
          text-align: center;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
          transition: transform 0.3s;
          border-bottom: 4px solid ${secondaryColor};
        }

        .achievement-card:hover {
          transform: translateY(-3px);
        }

        .achievement-image {
          width: 100%;
          height: 180px;
          margin-bottom: 15px;
          border-radius: 12px;
          overflow: hidden;
        }

        .achievement-image img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          background: #f5f5f5;
        }

        .year {
          display: inline-block;
          color: white;
          padding: 4px 16px;
          border-radius: 25px;
          font-size: 12px;
          font-weight: 600;
          margin-bottom: 12px;
        }

        .achievement-card h4 {
          font-size: 18px;
          color: #1e293b;
          margin-bottom: 10px;
        }

        /* Videos Grid */
        .videos-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 30px;
          margin-bottom: 30px;
        }

        .video-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
          transition: transform 0.3s;
        }

        .video-card:hover {
          transform: translateY(-3px);
        }

        .video-card iframe {
          width: 100%;
          height: 200px;
          border: none;
        }

        .video-card h4 {
          padding: 15px;
          margin: 0;
          font-size: 16px;
          color: #1e293b;
          text-align: center;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .container {
            padding: 50px 20px;
          }
          h2 {
            font-size: 28px;
          }
        }

        @media (max-width: 768px) {
          .page-banner {
            height: 300px;
          }
          .banner-overlay h1 {
            font-size: 32px;
          }
          .container {
            padding: 40px 16px;
          }
          h2 {
            font-size: 24px;
          }
          .overview {
            padding: 25px;
          }
          .sports-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          .achievements-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          .videos-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          .achievement-image {
            height: 150px;
          }
        }

        @media (max-width: 480px) {
          .page-banner {
            height: 250px;
          }
          .banner-overlay h1 {
            font-size: 28px;
          }
          .overview {
            padding: 20px;
          }
        }
      `}</style>
    </div>
  );
}

export default Sports;